import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { QuoteSearchDataGrid } from '@pages/Quotes/QuotesTable/QuotesDataGrid';
import { Tabs, QuoteTabOptions, TabPanel } from '@components/tabs';
import { QuoteDataGridType, TabValue } from '@pages/Quotes/QuotesTable/DataGrid.constants';
import { useQuoteMetadata } from '@services/quotes';
import { QuoteType } from '@models/quotes';
import { useStore } from '@store/GlobalStore';
import { Layout } from '@layout/Layout';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { shallow } from 'zustand/shallow';
import { PagePadding } from '@layout/Layout.constants';
import { IndicationsDataGrid } from '@pages/Quotes/IndicationsTable/IndicationsDataGrid';
import { useIndicationMetadata } from '@services/indications/useIndicationMetadata';
import Loading from '@components/Loading';

export const QuotesPage = () => {
  const { quoteMetadata, isLoading: isLoadingQuoteMetadata } = useQuoteMetadata({});
  const { indicationMetadata, isLoading: isLoadingIndicationMetadata } = useIndicationMetadata();

  const navigate = useNavigate();
  const isLoading = isLoadingQuoteMetadata || isLoadingIndicationMetadata;

  const [userAuth] = useStore((state) => [state.userAuth], shallow);

  const [searchParamsInTheUrl] = useSearchParams();
  const shouldShowIndications =
    userAuth?.isInternalUser || (indicationMetadata?.indications && indicationMetadata.indications > 0);

  const tabs: QuoteTabOptions[] = useMemo(() => {
    const _tabs = [
      {
        value: TabValue.Quotes,
        label: 'Quotes in Process',
        quantity: quoteMetadata?.quotes?.toString() || '0',
        quoteType: QuoteType.NewBusiness,
        queryParam: 'newBusiness',
      },
      {
        value: TabValue.Renewals,
        label: 'Renewals',
        quantity: quoteMetadata?.renewals?.toString() || '0',
        quoteType: QuoteType.Renewal,
        queryParam: 'renewal',
      },
    ];

    if (shouldShowIndications) {
      _tabs.push({
        value: TabValue.Indications,
        label: 'Indications',
        quantity: indicationMetadata?.indications?.toString() || '0',
        quoteType: QuoteType.Indication,
        queryParam: 'indication',
      });
    }

    return _tabs;
  }, [indicationMetadata?.indications, quoteMetadata?.quotes, quoteMetadata?.renewals, shouldShowIndications]);

  const getInitialQuoteType = useCallback(() => {
    // on first render
    const initialSearchParamsTab = tabs.filter(
      (el: QuoteTabOptions) => el.queryParam === searchParamsInTheUrl.get('quoteType'),
    );

    if (initialSearchParamsTab.length > 0) {
      return initialSearchParamsTab?.[0]?.value;
    }

    return tabs[TabValue.Quotes].value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialTabValue = getInitialQuoteType();
  const [tabValue, setTabValue] = useState<number>(initialTabValue);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    // necessary to 'reset' the table to load from store/not share common url params
    navigate('/quotes', { replace: true });
    setTabValue(newValue);
  };

  return (
    <Layout
      title='Quotes'
      config={{
        padding: PagePadding.TABLE_LIST_PAGE,
      }}
    >
      <>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Tabs tabOptions={tabs} value={tabValue} onChange={handleChange} />

            <TabPanel selectedTab={tabValue} value={tabs[TabValue.Quotes].value}>
              <QuoteSearchDataGrid viewType={QuoteDataGridType.QuoteTypeNewBusiness} />
            </TabPanel>
            <TabPanel selectedTab={tabValue} value={tabs[TabValue.Renewals].value}>
              <QuoteSearchDataGrid viewType={QuoteDataGridType.QuoteTypeRenewal} />
            </TabPanel>
            {shouldShowIndications && (
              <TabPanel selectedTab={tabValue} value={tabs[TabValue.Indications].value}>
                <IndicationsDataGrid />
              </TabPanel>
            )}
          </>
        )}
      </>
    </Layout>
  );
};
